'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import { formatLocalRange } from '@/lib/currency';

const RESOLVE_DELAY = 8500;

function BookingConfirmedCard({ t, costVal }: { t: ReturnType<typeof getTranslations>['hero']; costVal: string }) {
  return (
    <div className="glass-panel rounded-2xl overflow-hidden shadow-cyan-md border border-dispatch-cyan/30 float-gentle">
      <div className="px-6 py-4 bg-dispatch-cyan/10 border-b border-dispatch-cyan/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-dispatch-cyan/20 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <p className="text-dispatch-cyan font-mono font-bold text-sm">{t.bookingConfirmed}</p>
            <p className="text-dispatch-muted font-mono text-xs">{t.taskId}</p>
          </div>
        </div>
        <span className="chip">{t.enRoute}</span>
      </div>

      <div className="px-6 py-5 flex items-center gap-4 border-b border-dispatch-surface">
        <div className="relative">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_106f79f20-1763295098523.png"
            alt="Marcus Thompson, licensed plumber with tool belt, professional photo"
            width={52}
            height={52}
            className="rounded-full object-cover ring-2 ring-dispatch-cyan/40" />
          <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-dispatch-surface" />
        </div>
        <div className="flex-1">
          <p className="text-dispatch-text font-manrope font-semibold">Marcus Thompson</p>
          <p className="text-dispatch-muted font-mono text-xs">{t.plumber} · IL Lic #PL-88421</p>
          <div className="flex items-center gap-1 mt-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <svg key={s} width="10" height="10" viewBox="0 0 24 24" fill="#FFB800">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ))}
            <span className="text-dispatch-muted font-mono text-[10px] ml-1">4.9 · 312 {t.missions}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-dispatch-cyan font-manrope font-bold text-lg">8 min</p>
          <p className="text-dispatch-muted font-mono text-[10px]">{t.eta}</p>
        </div>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 gap-4">
        {[
          { label: t.service, value: t.serviceVal },
          { label: t.slot, value: t.slotVal },
          { label: t.address, value: t.addressVal },
          { label: t.cost, value: costVal },
        ].map((d) => (
          <div key={d.label}>
            <p className="text-dispatch-faint font-mono text-[10px] uppercase tracking-widest">{d.label}</p>
            <p className="text-dispatch-text font-dm-sans text-sm font-medium mt-0.5">{d.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { language, country } = useLanguage();
  const t = getTranslations(language).hero;
  const localCostVal = formatLocalRange(95, 140, country);

  const CODE_LINES = [
    { text: `// ${t.terminalTitle}`, cls: 'code-comment', delay: 200, duration: 600 },
    { text: `const job = bookService("${t.serviceVal}");`, cls: 'code-keyword', delay: 1000, duration: 1100 },
    { text: `job.setSlot("${t.slotVal}");`, cls: 'code-fn', delay: 2300, duration: 1000 },
    { text: `job.setAddress("${t.addressVal}");`, cls: 'code-string', delay: 3500, duration: 1200 },
    { text: `const match = await findPro(job);`, cls: 'code-fn', delay: 4900, duration: 1000 },
    { text: `// ✓ Marcus T. · 4.9★ · 8 min`, cls: 'code-comment', delay: 6100, duration: 800 },
    { text: `confirm() → tech ${t.enRoute} 🚐`, cls: 'code-success', delay: 7100, duration: 1000 },
  ];

  const [typedLines, setTypedLines] = useState<number[]>([]);
  const [resolved, setResolved] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    setTypedLines([]);
    setResolved(false);
    setShowBadges(false);
    timers.current.forEach(clearTimeout);
    timers.current = [];

    CODE_LINES.forEach((line, i) => {
      const timer = setTimeout(() => {
        setTypedLines((prev) => [...prev, i]);
      }, line.delay);
      timers.current.push(timer);
    });

    const resolveTimer = setTimeout(() => {
      setResolved(true);
      setTimeout(() => setShowBadges(true), 400);
      window.dispatchEvent(new Event('hero-resolved'));
    }, RESOLVE_DELAY);
    timers.current.push(resolveTimer);

    return () => timers.current.forEach(clearTimeout);
  }, [language]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(0,212,255,0.15) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at 50% 40%, black 30%, transparent 75%)'
        }} />

      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[400px] rounded-full bg-dispatch-cyan/5 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left: Copy + CTAs */}
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-dispatch-cyan badge-pulse" />
              <span className="text-dispatch-cyan font-mono text-xs tracking-widest uppercase">
                {t.badge}
              </span>
            </div>

            <h1
              className="font-manrope font-extrabold leading-[0.88] tracking-tighter"
              style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)' }}>
              <span className="block text-dispatch-text">{t.headline1}</span>
              <span className="block text-dispatch-text">{t.headline2}</span>
              <span className="block text-light-scan">{t.headline3}</span>
            </h1>

            <p className="text-dispatch-muted text-lg font-dm-sans leading-relaxed max-w-md">
              {t.subtitle}
            </p>

            <div
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 ${
                showBadges ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}>
              <a
                href="#download"
                className="group flex items-center gap-3 px-6 py-4 bg-dispatch-cyan text-dispatch-bg rounded-2xl font-bold font-mono text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-md hover:shadow-cyan-lg">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.42.07 2.38.74 3.2.8 1.22-.24 2.39-.93 3.72-.84 1.58.13 2.77.77 3.54 1.96-3.27 1.96-2.49 6.27.54 7.48-.65 1.72-1.49 3.43-3 4.46zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                {t.appStore}
              </a>
              <a
                href="#download"
                className="group flex items-center gap-3 px-6 py-4 border border-dispatch-cyan/40 text-dispatch-text rounded-2xl font-bold font-mono text-sm tracking-wide hover:bg-dispatch-cyan/10 hover:border-dispatch-cyan transition-all duration-300">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.18 23.76c.3.17.65.18.97.04L14.76 12 3.15.2C2.83.06 2.48.07 2.18.24A1.5 1.5 0 001.5 1.5v21c0 .55.28 1.04.68 1.26zm16.57-11.3l-2.7-1.56-3.13 3.13 3.13 3.12 2.71-1.56a1.5 1.5 0 000-3.13zm-15.6 8.9l9.35-9.36-2.76-2.76L1.5 19.1v1.77l2.65 1.49zM3.15.2l9.35 9.36-2.76 2.76L1.5 3.13V1.36L3.15.2z" />
                </svg>
                {t.googlePlay}
              </a>
            </div>

            <div
              className={`flex gap-6 transition-all duration-700 delay-200 ${
                showBadges ? 'opacity-100' : 'opacity-0'
              }`}>
              {[
                { n: t.stat1n, l: t.stat1l },
                { n: t.stat2n, l: t.stat2l },
                { n: t.stat3n, l: t.stat3l },
              ].map((s) => (
                <div key={s.n} className="flex flex-col gap-0.5">
                  <span className="text-dispatch-cyan font-manrope font-bold text-lg">{s.n}</span>
                  <span className="text-dispatch-faint font-mono text-[10px] uppercase tracking-widest">{s.l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Code terminal → booking confirmation */}
          <div className="relative">
            <div
              className={`transition-all duration-700 ${
                resolved ? 'opacity-0 scale-95 absolute inset-0' : 'opacity-100 scale-100'
              }`}>
              <div className="glass-panel rounded-2xl overflow-hidden shadow-card border border-dispatch-cyan/20">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-dispatch-surface bg-dispatch-surface/40">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-green-500/60" />
                  <span className="ml-3 text-dispatch-faint font-mono text-xs tracking-wide">{t.terminalTitle}</span>
                </div>

                <div className="p-6 font-mono text-sm leading-8 min-h-[280px]">
                  {CODE_LINES.map((line, i) => (
                    <div
                      key={i}
                      className={`typewriter-line ${line.cls} ${
                        typedLines.includes(i) ? 'typing' : ''
                      }`}
                      style={{
                        '--duration': `${line.duration}ms`,
                        '--steps': Math.max(line.text.length, 1)
                      } as React.CSSProperties}>
                      {line.text}
                    </div>
                  ))}
                  {typedLines.length > 0 && !resolved && (
                    <span className="cursor-blink text-dispatch-cyan font-mono">&nbsp;</span>
                  )}
                </div>

                <div className="absolute top-0 left-0 w-full h-8 bg-gradient-to-b from-dispatch-cyan/5 to-transparent scan-line pointer-events-none" />
              </div>
            </div>

            <div
              className={`transition-all duration-700 ${
                resolved ? 'opacity-100 scale-100' : 'opacity-0 scale-105 pointer-events-none'
              }`}>
              <BookingConfirmedCard t={t} costVal={localCostVal} />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dispatch-bg to-transparent pointer-events-none" />
    </section>
  );
}